interface Step2Data {
  participants: string;
  channels: string;
  city: string;
  venue: string;
}

interface Step2Props {
  data: Step2Data;
  onChange: (data: Partial<Step2Data>) => void;
  onNext: () => void;
  onBack: () => void;
}

const PARTICIPANT_RANGES = [
  { value: 'ate-50', label: 'Até 50', icon: 'ri-user-line', desc: 'Pequenos grupos' },
  { value: '50-150', label: '50 – 150', icon: 'ri-team-line', desc: 'Eventos médios' },
  { value: '150-300', label: '150 – 300', icon: 'ri-group-line', desc: 'Médio porte' },
  { value: '300-500', label: '300 – 500', icon: 'ri-group-2-line', desc: 'Grande porte' },
  { value: '500-2000', label: '500 – 2.000', icon: 'ri-community-line', desc: 'Eventos de grande escala' },
  { value: 'acima-2000', label: 'Acima de 2.000', icon: 'ri-building-4-line', desc: 'Mega eventos' },
];

const CHANNEL_OPTIONS = [
  { value: '1', label: '1 canal', desc: 'Um único conteúdo para todos' },
  { value: '2', label: '2 canais', desc: 'Dois idiomas ou duas trilhas' },
  { value: '3', label: '3 canais', desc: 'Três trilhas / idiomas simultâneos' },
  { value: '4+', label: '4 ou mais', desc: 'Multi-track avançado' },
];

const CITIES = [
  'São Paulo', 'Rio de Janeiro', 'Brasília', 'Belo Horizonte', 'Curitiba',
  'Porto Alegre', 'Salvador', 'Recife', 'Fortaleza', 'Manaus',
  'Belém', 'Goiânia', 'Florianópolis', 'Campinas', 'Natal',
  'Outro',
];

export default function Step2Participants({ data, onChange, onNext, onBack }: Step2Props) {
  const canProceed = data.participants !== '' && data.channels !== '' && data.city !== '';

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary mb-2">
          Quantas pessoas e onde?
        </h2>
        <p className="font-body text-gray-500 text-base">
          Nos diga o porte do evento e a cidade para dimensionar o sistema corretamente.
        </p>
      </div>

      {/* Participants */}
      <div className="mb-8">
        <label className="block font-body text-sm font-semibold text-secondary mb-4">
          Número estimado de participantes <span className="text-primary">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PARTICIPANT_RANGES.map((range) => (
            <button
              key={range.value}
              type="button"
              onClick={() => onChange({ participants: range.value })}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group text-left ${
                data.participants === range.value
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-primary/40 hover:bg-gray-50'
              }`}
            >
              <div
                className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors duration-200 ${
                  data.participants === range.value ? 'bg-primary/15' : 'bg-gray-100 group-hover:bg-primary/10'
                }`}
              >
                <i
                  className={`${range.icon} text-base transition-colors duration-200 ${
                    data.participants === range.value ? 'text-primary' : 'text-gray-500 group-hover:text-primary'
                  }`}
                ></i>
              </div>
              <div>
                <p
                  className={`font-body font-bold text-sm ${
                    data.participants === range.value ? 'text-primary' : 'text-secondary'
                  }`}
                >
                  {range.label}
                </p>
                <p className="font-body text-xs text-gray-400 hidden sm:block">{range.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Channels */}
      <div className="mb-8">
        <label className="block font-body text-sm font-semibold text-secondary mb-4">
          Quantos canais simultâneos você precisa? <span className="text-primary">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CHANNEL_OPTIONS.map((ch) => (
            <button
              key={ch.value}
              type="button"
              onClick={() => onChange({ channels: ch.value })}
              className={`p-4 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer ${
                data.channels === ch.value
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-primary/40'
              }`}
            >
              <p
                className={`font-heading font-black text-2xl mb-1 ${
                  data.channels === ch.value ? 'text-primary' : 'text-secondary'
                }`}
              >
                {ch.value}
              </p>
              <p className="font-body text-xs text-gray-500 leading-snug">{ch.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* City + Venue */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
        <div>
          <label className="block font-body text-sm font-semibold text-secondary mb-2">
            Cidade do evento <span className="text-primary">*</span>
          </label>
          <select
            value={data.city}
            onChange={(e) => onChange({ city: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-body text-gray-800 bg-white focus:border-primary focus:outline-none transition-colors cursor-pointer"
          >
            <option value="">Selecione a cidade...</option>
            {CITIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-body text-sm font-semibold text-secondary mb-2">
            Local / Venue (opcional)
          </label>
          <input
            type="text"
            value={data.venue}
            onChange={(e) => onChange({ venue: e.target.value })}
            placeholder="Ex.: Expo Center Norte, WTC, Hotel..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-body text-gray-800 bg-white focus:border-primary focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-body font-semibold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-left-line"></i>
          </div>
          Voltar
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          Continuar
          <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-arrow-right-line text-white"></i>
          </div>
        </button>
      </div>
    </div>
  );
}